import {NumberGroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/NumberPropertyTypeStrategy/enum/NumberGroupGranularityType'
import {
  bucketNumberByBinSize,
  bucketNumberByEdges,
  buildNumberBin,
  buildNumberBinFromEdges,
  normalizeNumberBinEdges,
  NUMBER_BIN_SIZE_BY_GRANULARITY,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/NumberPropertyTypeStrategy/numberBin'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IGroupGranularityDescriptor} from '@toolify/client/src/modules/databases/utils/groupSortModes'

export const NUMBER_GRANULARITY_FULL_LABEL_KEYS: Record<NumberGroupGranularityType, string> = {
  [NumberGroupGranularityType.Exact]: 'database.header.granularityNumberExact',
  [NumberGroupGranularityType.Bin1]: 'database.header.granularityNumberBin1',
  [NumberGroupGranularityType.Bin10]: 'database.header.granularityNumberBin10',
  [NumberGroupGranularityType.Bin100]: 'database.header.granularityNumberBin100',
  [NumberGroupGranularityType.Bin1000]: 'database.header.granularityNumberBin1000',
  [NumberGroupGranularityType.Custom]: 'database.header.granularityNumberCustom',
}

export const NUMBER_GRANULARITY_SHORT_LABEL_KEYS: Record<NumberGroupGranularityType, string> = {
  [NumberGroupGranularityType.Exact]: '',
  [NumberGroupGranularityType.Bin1]: 'database.header.granularityNumberBin1Short',
  [NumberGroupGranularityType.Bin10]: 'database.header.granularityNumberBin10Short',
  [NumberGroupGranularityType.Bin100]: 'database.header.granularityNumberBin100Short',
  [NumberGroupGranularityType.Bin1000]: 'database.header.granularityNumberBin1000Short',
  [NumberGroupGranularityType.Custom]: 'database.header.granularityNumberCustomShort',
}

export function getNumberBinSize(granularity: unknown): number | null {
  return NUMBER_BIN_SIZE_BY_GRANULARITY[granularity as NumberGroupGranularityType] ?? null
}

export function isCustomNumberGranularity(granularity: unknown): boolean {
  return granularity === NumberGroupGranularityType.Custom
}

export const numberGroupGranularityDescriptor: IGroupGranularityDescriptor = {
  getDefault: () => NumberGroupGranularityType.Exact,
  getValues: () => Object.values(NumberGroupGranularityType),
  getFullLabelKey: (value: string): string =>
    NUMBER_GRANULARITY_FULL_LABEL_KEYS[value as NumberGroupGranularityType] ?? '',
  getShortLabelKey: (value: string): string =>
    NUMBER_GRANULARITY_SHORT_LABEL_KEYS[value as NumberGroupGranularityType] ?? '',
  icon: IconNameType.LayerGroup,
  hasEditableOptions: (granularity) => isCustomNumberGranularity(granularity),
  bucketValue: (value, fullValue, granularity, _property, options) => {
    if(isCustomNumberGranularity(granularity)) {
      const edges = normalizeNumberBinEdges(options?.edges)
      if(!edges.length) {
        return null
      }
      const index = bucketNumberByEdges(fullValue ?? value, edges)
      if(index === null) {
        return {
          value: null,
          fullValue: null,
        }
      }
      const bin = buildNumberBinFromEdges(index, edges)
      return {
        value: bin,
        fullValue: bin,
      }
    }
    const size = getNumberBinSize(granularity)
    if(size === null) {
      return {
        value,
        fullValue,
      }
    }
    const from = bucketNumberByBinSize(fullValue ?? value, size)
    if(from === null) {
      return {
        value: null,
        fullValue: null,
      }
    }
    const bin = buildNumberBin(from, size)
    return {
      value: bin,
      fullValue: bin,
    }
  },
}
